import { captureException } from '@sentry/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { savedSeriesService } from '@/services/database';
import type { SavedSeries, SavedSeriesInput, SavedSeriesSectionKey } from '@/types';

const savedSeriesQueryKey = (sectionKey: SavedSeriesSectionKey) => ['savedSeries', sectionKey] as const;

export const useSavedSeries = (sectionKey: SavedSeriesSectionKey) => {
    const queryClient = useQueryClient();
    const queryKey = savedSeriesQueryKey(sectionKey);
    const query = useQuery({
        enabled: Boolean(sectionKey),
        queryFn: async ({ signal }) => {
            try {
                return await savedSeriesService.list({ sectionKey }, signal);
            } catch (error) {
                captureException(error);
                throw error;
            }
        },
        queryKey,
    });
    const createMutation = useMutation({
        mutationFn: async (input: SavedSeriesInput) => {
            try {
                return await savedSeriesService.create(input);
            } catch (error) {
                captureException(error);
                throw error;
            }
        },
        onSuccess: (created) => {
            queryClient.setQueryData<SavedSeries[]>(queryKey, (current = []) => [created, ...current]);
        },
    });
    const deleteMutation = useMutation({
        mutationFn: async (id: string) => {
            try {
                return await savedSeriesService.delete(id);
            } catch (error) {
                captureException(error);
                throw error;
            }
        },
        onSuccess: ({ id }) => {
            queryClient.setQueryData<SavedSeries[]>(queryKey, (current = []) =>
                current.filter((series) => series.id !== id),
            );
        },
    });
    const updateMutation = useMutation({
        mutationFn: async ({ id, updates }: { id: string; updates: Partial<SavedSeriesInput> }) => {
            try {
                return await savedSeriesService.update(id, updates);
            } catch (error) {
                captureException(error);
                throw error;
            }
        },
        onSuccess: (updated) => {
            queryClient.setQueryData<SavedSeries[]>(queryKey, (current = []) =>
                current.map((series) => (series.id === updated.id ? updated : series)),
            );
        },
    });

    return {
        createSavedSeries: createMutation.mutateAsync,
        deleteSavedSeries: deleteMutation.mutateAsync,
        deletingSavedSeriesId: deleteMutation.variables,
        loadingSavedSeries: query.isLoading,
        savedSeries: query.data ?? [],
        savingSeries: createMutation.isPending,
        updateSavedSeries: (id: string, updates: Partial<SavedSeriesInput>) =>
            updateMutation.mutateAsync({ id, updates }),
        updatingSavedSeriesId: updateMutation.variables?.id,
    };
};
